// ── Effect Loop — Core effect system infrastructure ──
// Central animation loop, texture cache, shared helpers

import * as THREE from 'three';

// Effects use layer 1 so the raycaster (layer 0 only) skips them entirely.
// The camera must have layer 1 enabled to still render effects.
export const EFFECT_LAYER = 1;

// ---------------------------------------------------------------------------
// Central effect animation loop — replaces hundreds of individual RAF chains
// ---------------------------------------------------------------------------

export interface ActiveEffect {
  update: (now: number) => boolean; // returns false when done
}

const activeEffects: ActiveEffect[] = [];
let effectLoopRunning = false;

function startEffectLoop(): void {
  if (effectLoopRunning) return;
  effectLoopRunning = true;

  function tick(): void {
    const now = performance.now();
    // Iterate backwards so we can splice without index issues
    for (let i = activeEffects.length - 1; i >= 0; i--) {
      if (!activeEffects[i].update(now)) {
        activeEffects.splice(i, 1);
      }
    }
    if (activeEffects.length === 0) {
      // Release the flag synchronously so a same-frame addEffect starts a fresh RAF
      // instead of waiting a full frame for this loop to exit on the next tick.
      effectLoopRunning = false;
      return;
    }
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}

export function addEffect(effect: ActiveEffect): void {
  activeEffects.push(effect);
  startEffectLoop();
}

// ---------------------------------------------------------------------------
// Texture cache — avoid recreating canvas textures every call
// ---------------------------------------------------------------------------

export const effectTextureCache = new Map<string, THREE.Texture>();

/**
 * Generate a soft gradient sprite texture using a canvas.
 * Results are cached by key so repeated calls reuse the same texture.
 */
export function createSpriteTexture(
  size: number,
  color1: string,
  color2: string,
  type: 'circle' | 'spark' | 'smoke',
): THREE.Texture {
  const key = `${size}_${color1}_${color2}_${type}`;
  const cached = effectTextureCache.get(key);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  const cx = size / 2;
  const cy = size / 2;

  if (type === 'circle') {
    // Soft radial gradient — bright center, transparent edges
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, cx);
    grad.addColorStop(0, color1);
    grad.addColorStop(0.4, color2);
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);
  } else if (type === 'spark') {
    // Elongated diamond / streak shape
    ctx.save();
    ctx.translate(cx, cy);
    const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, cx);
    grad.addColorStop(0, color1);
    grad.addColorStop(0.5, color2);
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.ellipse(0, 0, cx, cy * 0.35, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  } else {
    // 'smoke' — noisy cloud-like shape with pseudo-perlin noise
    const imageData = ctx.createImageData(size, size);
    const data = imageData.data;

    // Parse color1 into rgb for tinting
    ctx.fillStyle = color1;
    ctx.fillRect(0, 0, 1, 1);
    const sample = ctx.getImageData(0, 0, 1, 1).data;
    const r1 = sample[0], g1 = sample[1], b1 = sample[2];

    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const dx = (x - cx) / cx;
        const dy = (y - cy) / cy;
        const dist = Math.sqrt(dx * dx + dy * dy);

        // Layered noise approximation
        const n1 = Math.sin(x * 0.3) * Math.cos(y * 0.3) * 0.5 + 0.5;
        const n2 = Math.sin(x * 0.7 + 3.1) * Math.cos(y * 0.5 + 1.7) * 0.25 + 0.25;
        const n3 = Math.sin(x * 1.3 + 0.8) * Math.cos(y * 1.1 + 2.5) * 0.125 + 0.125;
        const noise = (n1 + n2 + n3) / (0.5 + 0.25 + 0.125);

        // Radial falloff with noisy edge
        const falloff = Math.max(0, 1 - dist * (0.8 + noise * 0.4));
        const alpha = falloff * noise * 255;

        const idx = (y * size + x) * 4;
        data[idx] = r1;
        data[idx + 1] = g1;
        data[idx + 2] = b1;
        data[idx + 3] = Math.min(255, alpha);
      }
    }
    ctx.putImageData(imageData, 0, 0);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  effectTextureCache.set(key, texture);
  return texture;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Mark an object as an effect (layer 1) so raycaster on layer 0 skips it.
 *  Also sets userData.isEffect for secondary filter in performHitscan(). */
export function markEffect(obj: THREE.Object3D): void {
  obj.layers.set(EFFECT_LAYER);
  obj.userData.isEffect = true;
}

export function disposeMesh(mesh: THREE.Object3D, scene: THREE.Scene): void {
  scene.remove(mesh);
  if (mesh instanceof THREE.Mesh || mesh instanceof THREE.Sprite) {
    if (mesh.geometry && 'dispose' in mesh.geometry) {
      (mesh.geometry as THREE.BufferGeometry).dispose();
    }
    if (mesh.material) {
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((m: THREE.Material | undefined) => m?.dispose());
      } else {
        (mesh.material as THREE.Material).dispose();
      }
    }
  }
  if (mesh instanceof THREE.PointLight) {
    mesh.dispose();
  }
}

export function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/** Dispose all cached effect textures and clear the cache. Call on game dispose or level unload. */
export function disposeEffectTextureCache(): void {
  for (const texture of effectTextureCache.values()) {
    // Explicitly null canvas references to allow GC of the underlying canvas element
    if (texture.source && texture.source.data) {
      texture.source.data = null;
    }
    texture.dispose();
  }
  effectTextureCache.clear();
}
